// SCR-STAFF-103 — نمای ۳۶۰ و خط زمانی مراجعه‌کننده (AP-PAT-02، US-PAT-080): رویدادهای همه ماژول‌ها (PAT، SCH و بعداً BIL، CLN،
// MSG) با مرتب‌سازی نزولی زمان و صفحه‌بندی Cursor؛ هر ماژول فقط آیتم‌های مجاز کاربر را می‌دهد (AC-PAT-080.2)؛ ماژول کند در
// «partial» اعلام و بقیه نمایش داده می‌شوند (AC-PAT-080.3). آیتم حساس (خدمت روانپزشکی) بدون خلاصه.
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { unwrap, type PatientTimelineItem } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { EmptyState, ErrorState, LoadingState, toJalaliDateTime } from '@phoenix/ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router';
import { patKeys } from './common';

export const SCR_STAFF_103 = 'SCR-STAFF-103';
const PAGE = 20;

export function TimelineSummary({ item }: { item: PatientTimelineItem }) {
  const { t } = useTranslation('pat');
  if (item.summary === null || item.summary === undefined) {
    return item.sensitivity === 'Clinical' || item.sensitivity === 'HighlySensitive' ? (
      <Stack direction="row" spacing={0.5} alignItems="center" component="span">
        <LockOutlinedIcon fontSize="inherit" />
        <span>{t('pat:timeline.sensitive')}</span>
      </Stack>
    ) : null;
  }
  return <span>{item.type === 'consent' ? t(`pat:consents.types.${item.summary}`) : item.summary}</span>;
}

export function PatientTimeline({ patientId }: { patientId: string }) {
  const { t } = useTranslation('pat');
  const { tenantApi, session } = useTenantSession();
  const timeline = useInfiniteQuery({
    queryKey: patKeys.timeline(session?.tenantId, patientId),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(
        tenantApi.GET('/api/v1/patients/patients/{id}/timeline', {
          params: { path: { id: patientId }, query: { limit: PAGE, ...(pageParam ? { cursor: pageParam } : {}) } },
        }),
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  if (timeline.isPending) {
    return <LoadingState rows={4} />;
  }
  if (timeline.error) {
    return <ErrorState error={timeline.error} onRetry={() => void timeline.refetch()} />;
  }
  const items = timeline.data.pages.flatMap((page) => page.items);
  const partial = [...new Set(timeline.data.pages.flatMap((page) => page.partial))];

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-screen-id={SCR_STAFF_103}>
      <Typography variant="h2" gutterBottom>
        {t('pat:timeline.title')}
      </Typography>
      {partial.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }} data-testid="timeline-partial">
          {t('pat:header.partial', { sources: partial.join(t('pat:list_separator')) })}
        </Alert>
      )}
      {items.length === 0 ? (
        <EmptyState titleKey="pat:timeline.empty" />
      ) : (
        <List aria-label={t('pat:timeline.title')} data-testid="patient-timeline">
          {items.map((item) => (
            <ListItem key={`${item.source}:${item.id}`} divider data-testid="timeline-item" data-source={item.source}>
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center" component="span">
                    <Chip size="small" variant="outlined" label={t(`pat:timeline.sources.${item.source}`, { defaultValue: item.source })} />
                    {item.link ? (
                      <Link component={RouterLink} to={item.link}>
                        {t(item.titleKey)}
                      </Link>
                    ) : (
                      <span>{t(item.titleKey)}</span>
                    )}
                  </Stack>
                }
                secondary={
                  <Stack direction="row" spacing={1} component="span">
                    <span>{toJalaliDateTime(item.occurredAt)}</span>
                    <TimelineSummary item={item} />
                  </Stack>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
      {timeline.hasNextPage && (
        <Box sx={{ mt: 1 }}>
          <Button onClick={() => void timeline.fetchNextPage()} disabled={timeline.isFetchingNextPage} data-testid="timeline-more">
            {t('pat:timeline.more')}
          </Button>
        </Box>
      )}
    </Paper>
  );
}
